import { useMutation } from '@apollo/client';
import { useState } from 'react';

import Button from '@/componentsv2/Button';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import DeclineConfirmation from '../components/EngagementHub/DeclineConfirmation';
import type { ViewerRole } from '../components/EngagementHub/roles';
import {
  canAcceptOfferLive,
  canDeclineOfferLive,
  canMakeOfferFor,
  canWithdrawRequestLive,
} from '../components/EngagementHub/roles';
import MakeOfferDrawer from './MakeOfferDrawer';
import {
  ACCEPT_ENGAGEMENT_OFFER,
  DECLINE_ENGAGEMENT_OFFER,
  WITHDRAW_ENGAGEMENT_REQUEST,
} from './operations';
import { UNKNOWN_PARTY, partyIdentity } from './parties';
import type { EngagementView } from './viewModel';

interface ActionStripProps {
  view: EngagementView;
  viewerRole: ViewerRole;
  viewerUserId: string;
  viewerProfileId: string | null;
}

type Expanded = 'decline' | 'withdraw' | null;

/** `DeclineEngagementOfferInput.note` is capped at 255 characters server-side. */
const DECLINE_NOTE_MAX = 255;

/**
 * The state-dependent actions the Hub can honor: the expert's offer and
 * decline, and the customer creator's accept and withdraw.
 *
 * Decline and withdraw are the two pre-activation exits (ENG-5604 G1). Both
 * return the engagement, so Apollo's normalized cache moves the Hub to
 * `canceled` on its own — and both are visible past this screen: the api emits
 * `evOfferDeclined` and a `request_withdrawn` notification to the other side.
 *
 * The governance decision is not here. The prototype puts it in this row; live
 * puts it in the Overview banner that asks for it (ENG-5752), alongside the
 * terms it turns on.
 */
export default function ActionStrip({
  view,
  viewerRole,
  viewerUserId,
  viewerProfileId,
}: ActionStripProps): JSX.Element | null {
  const [offerOpen, setOfferOpen] = useState(false);
  // Bumped on each open so the drawer remounts with clean state. Keyed on the
  // count rather than on `open` so closing keeps the same instance and its
  // slide-out animation; a stale `submitError` must not greet a fresh attempt.
  const [offerSession, setOfferSession] = useState(0);
  const [expanded, setExpanded] = useState<Expanded>(null);
  const [error, setError] = useState<string | null>(null);
  const [acceptOffer, { loading: accepting }] = useMutation(ACCEPT_ENGAGEMENT_OFFER);
  const [declineOffer, { loading: declining }] = useMutation(DECLINE_ENGAGEMENT_OFFER);
  const [withdrawRequest, { loading: withdrawing }] = useMutation(WITHDRAW_ENGAGEMENT_REQUEST);

  const showMakeOffer = canMakeOfferFor(view.status, viewerRole);
  const showDecline = canDeclineOfferLive(view.status, viewerRole);
  const showAccept = canAcceptOfferLive(
    view.status,
    view.awaiting,
    view.createdByUserId,
    viewerUserId,
    view.governanceFeedback !== null
  );
  const showWithdraw = canWithdrawRequestLive(
    view.status,
    view.awaiting,
    view.createdByUserId,
    viewerUserId
  );
  if (!showMakeOffer && !showDecline && !showAccept && !showWithdraw) {
    return null;
  }

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
      setExpanded(null);
    } catch (err) {
      setError(getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? fallback);
    }
  }

  function handleDecline(note: string) {
    if (note.length > DECLINE_NOTE_MAX) {
      setError(`Keep the reason to ${DECLINE_NOTE_MAX} characters or fewer.`);
      return;
    }
    // Omit the key entirely when the expert declined without a reason, rather
    // than sending an explicit null the api would have to interpret.
    const input = note ? { engagementId: view.id, note } : { engagementId: view.id };
    void run(() => declineOffer({ variables: { input } }), 'This request could not be declined.');
  }

  function toggle(mode: Exclude<Expanded, null>) {
    setError(null);
    setExpanded((current) => (current === mode ? null : mode));
  }

  // Decline is expert-only, and the api denies the expert `primaryProject`, so
  // `customerIdentities` (ENG-5749) is what names the creator here. A creator
  // it cannot name is addressed by role, never as "A participant".
  const creator = partyIdentity(view, viewerUserId)(view.createdByUserId);
  const creatorName = creator && creator !== UNKNOWN_PARTY ? creator.name : 'the customer';

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap gap-12">
        {showMakeOffer ? (
          <Button
            onClick={() => {
              setOfferSession((n) => n + 1);
              setOfferOpen(true);
            }}
          >
            Make offer
          </Button>
        ) : null}
        {showAccept ? (
          <Button
            onClick={() =>
              void run(
                () => acceptOffer({ variables: { input: { engagementId: view.id } } }),
                'This offer could not be accepted.'
              )
            }
            disabled={accepting}
          >
            Accept offer
          </Button>
        ) : null}
        {showDecline ? (
          <Button variant="secondary" onClick={() => toggle('decline')} disabled={declining}>
            Decline
          </Button>
        ) : null}
        {showWithdraw ? (
          <Button variant="secondary" onClick={() => toggle('withdraw')} disabled={withdrawing}>
            Withdraw request
          </Button>
        ) : null}
      </div>

      {expanded === 'decline' ? (
        <DeclineConfirmation
          creatorName={creatorName}
          busy={declining}
          onConfirm={handleDecline}
          onCancel={() => setExpanded(null)}
        />
      ) : null}

      {expanded === 'withdraw' ? (
        <WithdrawConfirm
          busy={withdrawing}
          onCancel={() => setExpanded(null)}
          onConfirm={() =>
            void run(
              () => withdrawRequest({ variables: { input: { engagementId: view.id } } }),
              'This request could not be withdrawn.'
            )
          }
        />
      ) : null}

      {error ? <p className="text-red-600 body-2">{error}</p> : null}

      {showMakeOffer ? (
        <MakeOfferDrawer
          key={offerSession}
          open={offerOpen}
          onClose={() => setOfferOpen(false)}
          view={view}
          viewerUserId={viewerUserId}
          viewerProfileId={viewerProfileId}
        />
      ) : null}
    </div>
  );
}

/**
 * Withdraw carries no note on the wire (`WithdrawEngagementRequestInput` holds
 * only the id), so this is a plain confirm rather than the note expander the
 * decline flow uses — asking for a reason with nowhere to send it would be
 * theatre.
 */
function WithdrawConfirm({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated px-16 py-16">
      <div className="flex flex-col gap-2">
        <span className="text-on-surface subtitle-2">Withdraw this request</span>
        <span className="text-on-surface-muted body-2">
          The engagement will be canceled and the expert notified. You can start a new request
          later.
        </span>
      </div>
      <div className="flex justify-end gap-8">
        <Button variant="tertiary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="secondary" size="small" onClick={onConfirm} disabled={busy}>
          Confirm withdrawal
        </Button>
      </div>
    </div>
  );
}
